// 認証Cookie（supplier-auth）を焼き付ける関数とオプション
// lib/auth.tsx から切り出したもの。tests/auth-cookie.test.js から直接 import して挙動を確かめるため、
// パスエイリアス（lib/...）や React などに依存させない。

import type { NextApiResponse } from "next";
import { serialize } from "cookie";
import type { SerializeOptions } from "cookie";

// ログイン状態の保持期間（7日）。
// 未指定だとセッションCookieになり、JWT が有効でもブラウザを閉じるたびに再ログイン（＋OTP）が必要になる（NITOEL-9541）。
// バックエンドの JWT 有効期限（tenant-backend/config/initializers/devise.rb: 7日12時間）より短くしておく。
export const AUTH_COOKIE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// cookieを焼き付ける関数
// https://nextjs.org/docs/api-routes/api-middlewares
// maxAge はミリ秒で受け取り、Expires を付けたうえで秒に直す（渡された options を書き換える）
export function setCookie(res: Pick<NextApiResponse, "setHeader">, name: string, value: unknown, options: SerializeOptions = {}) {
  const stringValue = typeof value === "object" ? "j:" + JSON.stringify(value) : String(value);

  if (options.maxAge) {
    options.expires = new Date(Date.now() + options.maxAge);
    options.maxAge /= 1000;
  }

  res.setHeader("Set-Cookie", serialize(name, stringValue, options));
}

// Cookie設定の共通オプションを生成する関数
// 本番環境ではSameSite=None; Secureを使用（メール経由のクロスサイトアクセス対応）
// 開発環境ではSameSite=Laxを使用（SameSite=NoneはSecure必須のため）
function getBaseCookieOptions(): SerializeOptions {
  const isProduction = process.env.NODE_ENV === "production";
  return {
    // jsからのcookieの取得を認めない(httpからのみ)
    httpOnly: true,
    // 本番環境ではクロスサイトリクエストでもcookieを送信、開発環境では同一サイトのみ
    sameSite: isProduction ? "none" : "lax",
    // 本番環境ではhttps環境じゃないとcookieを送信しない
    secure: isProduction,
    // cookieが有効となるpath
    path: "/",
  };
}

// ログイン時に認証Cookieを焼き付けるオプション
export function getCookieOptions(): SerializeOptions {
  return { ...getBaseCookieOptions(), maxAge: AUTH_COOKIE_MAX_AGE_MS };
}

// ログアウト時に認証Cookieを削除するオプション
// Max-Age は Expires より優先されるため maxAge を含めないこと（含めると削除されずログアウトできない）
export function getDeleteCookieOptions(): SerializeOptions {
  return {
    ...getBaseCookieOptions(),
    expires: new Date(0), // 過去の日付を設定してCookie削除（maxAge: -1は新しいcookieライブラリで-0.001になりエラー）
  };
}
